import { WardrobeState } from './fd-wardrobe-state.js?v=20261016-wardrobe-2';
import { StandardEvents } from '@shopify/events';
class FDWardrobe extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.cards = [...this.querySelectorAll('[data-choice]')];
    this.state = new WardrobeState(this.cards.map(card => ({ handle: card.dataset.handle, group: card.dataset.group, value: card.dataset.value, title: card.dataset.title, image: card.querySelector('img')?.currentSrc || card.querySelector('img')?.src })));
    this.slots = [...this.querySelectorAll('[data-slot]')];
    this.disclosure = this.querySelector('[data-summary-disclosure]');
    this.desktop = matchMedia('(min-width: 64em)');
    const setDisclosure = () => { this.disclosure.open = this.desktop.matches; this.updateSticky(); };
    this.desktop.addEventListener('change', setDisclosure, options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !this.desktop.matches && this.disclosure.open && this.disclosure.contains(event.target)) {
        this.disclosure.open = false; this.disclosure.querySelector('summary').focus();
      }
    }, options);
    this.disclosure.addEventListener('toggle', () => this.updateSticky(), options);
    document.addEventListener('scroll', () => this.updateSticky(), { ...options, passive: true, capture: true });
    window.addEventListener('resize', () => this.updateSticky(), options);
    setDisclosure();
    this.form = this.querySelector('form'); this.submit = this.querySelector('[ref=addToCartButton]');
    this.variants = JSON.parse(this.querySelector('[data-wardrobe-variants]').textContent);
    this.addEventListener('click', event => {
      if (this.pending) return;
      const card = event.target.closest('[data-choice]'); const remove = event.target.closest('button[data-remove]');
      if (card && this.state.choose(card.dataset.handle, card.dataset.group)) { this.resetSuccess(); this.update(); }
      if (remove) { const index = Number(remove.dataset.remove); const previous = this.state.item(index); this.state.remove(index); this.resetSuccess(); this.update(); if (!this.desktop.matches) this.disclosure.open = false; this.cards.find(item => item.dataset.handle === previous?.handle && item.dataset.group === previous?.group)?.focus(); }
    }, options);
    this.addEventListener('submit', event => {
      if (this.pending || !this.state.valid || this.dataset.available !== 'true' || !this.selectedVariant()?.available) {
        event.preventDefault(); event.stopImmediatePropagation(); this.update(); return;
      }
      // Rebuild from authoritative current state immediately before native FormData capture.
      this.syncProperties();
    }, { ...options, capture: true });
    this.addEventListener(StandardEvents.cartLinesUpdate, event => {
      if (event.action !== 'add' || !this.contains(event.target)) return;
      this.pending = true; this.update();
      event.promise?.then(({ detail }) => { if (!detail?.didError) this.state.markAdded(); }).catch(() => {}).finally(() => {
        // Run after the native purchase-state handler restores its controls.
        queueMicrotask(() => { if (this.isConnected) { this.pending = false; this.update(); } });
      });
    }, options);
    this.update();
  }
  resetSuccess() {
    const purchase = this.querySelector('fd-product-purchase');
    if (purchase?.dataset.state !== 'success' || this.state.added) return;
    purchase.dataset.state = 'idle';
    purchase.querySelector('[data-purchase-status]').textContent = '';
    purchase.querySelector('[ref=liveRegion]').textContent = '';
  }
  updateSticky() {
    if (this.stickyFrame) return;
    this.stickyFrame = requestAnimationFrame(() => {
      this.stickyFrame = null;
      const summary = this.querySelector('.fd-wardrobe__summary');
      const rect = summary.getBoundingClientRect();
      const top = parseFloat(getComputedStyle(summary).top) || 0;
      this.dataset.summaryStuck = String(rect.top <= top + rect.height && rect.bottom > top);
    });
  }
  syncProperties() {
    const values = this.state.valid ? this.state.properties() : {};
    this.querySelectorAll('[data-property]').forEach(input => { input.value = values[input.dataset.property] || ''; });
    this.form.querySelector('[name=id]').value = this.selectedVariant()?.id || '';
  }
  selectedVariant() { return this.variants?.find(v => Number(v.price) === this.state.price * 100); }
  update() {
    const state = this.state;
    const progress = this.querySelector('progress'); progress.value = state.count;
    this.querySelectorAll('[data-progress]').forEach(node => { node.textContent = `${state.count} / 7`; });
    this.querySelector('[data-special-count]').textContent = String(state.specialCount);
    this.querySelector('[data-pool-count]').textContent = String(state.additional);
    const tr = document.documentElement.lang.startsWith('tr');
    const message = state.valid ? (tr ? 'Seçiminiz tamamlandı. Eklediğiniz kokuları × ile çıkarabilirsiniz.' : 'Your selection is complete. Remove a fragrance with × to change it.') :
      (tr ? `${7-state.count} adet daha seçin. Aynı kokudan tekrar ekleyebilirsiniz.` : `Choose ${7-state.count} more. You may add the same fragrance again.`);
    this.querySelectorAll('[data-requirement]').forEach(node => { node.textContent = message; });
    this.querySelector('[data-incomplete]').hidden = state.valid;
    this.querySelector('[data-complete-guidance]').hidden = !state.valid;
    this.slots.forEach((slot, index) => {
      const item = state.item(index); slot.dataset.filled = String(Boolean(item));
      slot.querySelector('[data-slot-name]').textContent = item?.title || `${this.dataset.fragrance} ${index + 1}`;
      const image = slot.querySelector('img'); image.hidden = !item?.image;
      if (item?.image && image.getAttribute('src') !== item.image) image.src = item.image;
      const remove = slot.querySelector('button'); remove.hidden = !item; remove.disabled = Boolean(this.pending);
      remove.setAttribute('aria-label', `${this.dataset.removeLabel}: ${item?.title || ''}`);
    });
    this.cards.forEach(card => {
      const count = state.slots.filter(handle => handle === card.dataset.handle).length;
      const selected = count > 0;
      card.setAttribute('aria-pressed', String(selected));
      const unavailable = state.count === 7;
      card.disabled = Boolean(this.pending || unavailable);
      card.querySelector('[data-choice-label]').textContent = selected ? `${count} ${tr ? 'adet seçildi' : 'selected'} · +1` : `+1 ${this.dataset.select}`;
    });
    this.submit.disabled = Boolean(this.pending || !state.valid || this.dataset.available !== 'true' || !this.selectedVariant()?.available);
    const formatPrice = amount => `${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: Number.isInteger(amount) ? 0 : 2, maximumFractionDigits: 2 }).format(amount)} ₺`;
    const variant = this.selectedVariant();
    const compare = Number(variant?.compare_at_price);
    const hasDiscount = Number.isFinite(compare) && compare > state.price * 100;
    this.querySelectorAll('[data-wardrobe-price]').forEach(node => {
      node.textContent = formatPrice(state.price);
    });
    this.querySelectorAll('[data-wardrobe-compare]').forEach(node => {
      node.hidden = !hasDiscount;
      node.textContent = hasDiscount ? formatPrice(compare / 100) : '';
    });
    this.querySelector('[data-review]').hidden = !state.valid;
    this.querySelector('[data-review-names]').textContent = state.valid ? state.slots.map((_, i) => state.item(i).title).join(' · ') : '';
    this.syncProperties();
  }
  disconnectedCallback() { this.abort?.abort(); cancelAnimationFrame(this.stickyFrame); }
}
if (!customElements.get('fd-wardrobe')) customElements.define('fd-wardrobe', FDWardrobe);

